import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import GdprConsentModal from '../components/GdprConsentModal';
import { consentAPI, clientsAPI } from '../services/api';
import './Login.css';
import myshieldLogo from '../assets/myshield-logo.png';

// ── Helpers ────────────────────────────────────────────────────────────────

const delay = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms));

interface LoggedInUser {
  role: string;
  clientId?: number;
}

/**
 * Fetch the login background video URL for the given user.
 * Checks the client record via the API; falls back to static defaults.
 * Called during the blackout so the network request adds no visible latency.
 */
const fetchLoginVideoUrl = async (user?: LoggedInUser): Promise<string> => {
  if (!user) return '/background-video.mp4';
  if (user.clientId) {
    try {
      const res = await clientsAPI.getLoginVideo(user.clientId);
      if (res.data?.videoUrl) return res.data.videoUrl as string;
    } catch { /* fall through to default */ }
  }
  // All admin/staff roles use the default splash video
  return '/background-video.mp4';
};

// ── Component ──────────────────────────────────────────────────────────────

const Login: React.FC = () => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showGdpr, setShowGdpr] = useState(false);

  // MFA state
  const [mfaToken, setMfaToken]       = useState<string | null>(null);
  const [mfaCode, setMfaCode]         = useState('');
  const [mfaVisible, setMfaVisible]   = useState(false);

  // Transition states
  const [welcomeHidden, setWelcomeHidden] = useState(false);
  const [formVisible, setFormVisible]     = useState(false);
  const [overlayBlackout, setOverlayBlackout] = useState(false);
  const [showJwtNotice, setShowJwtNotice] = useState(false);

  const { login, verifyMfa, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.play().catch(() => {});
    }
  }, []);

  useEffect(() => {
    if (!localStorage.getItem('jwt-notice-acknowledged')) {
      setShowJwtNotice(true);
    }
  }, []);

  const handleJwtNoticeAcknowledge = () => {
    localStorage.setItem('jwt-notice-acknowledged', '1');
    setShowJwtNotice(false);
  };

  // ── Welcome splash → login form ──────────────────────────────────────────
  const handleEnter = () => {
    setWelcomeHidden(true);
    // Let the welcome overlay start fading before the form appears
    setTimeout(() => setFormVisible(true), 350);
  };

  // ── Login submit → video swap → GDPR ─────────────────────────────────────
  // ── Credential submit → MFA challenge ───────────────────────────────────
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(username, password);
      setLoading(false);

      if (result.mfaRequired && result.mfaToken) {
        // Slide to MFA step
        setFormVisible(false);
        setTimeout(() => {
          setMfaToken(result.mfaToken!);
          setMfaVisible(true);
        }, 400);
        return;
      }

      // Direct login fallback (MFA disabled)
      const loggedInUser = result.user!;
      await proceedAfterAuth(loggedInUser);
    } catch (err: any) {
      setLoading(false);
      setFormVisible(true);
      setError(err.message || 'Login failed. Please check your credentials.');
    }
  };

  // ── MFA code submit → video swap → GDPR ──────────────────────────────────
  const handleMfaSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const loggedInUser = await verifyMfa(mfaToken!, mfaCode);
      setLoading(false);
      setMfaVisible(false);
      await proceedAfterAuth(loggedInUser);
    } catch (err: any) {
      setLoading(false);
      setError(err.message || 'Incorrect code. Please try again.');
    }
  };

  // ── Shared post-auth animation ────────────────────────────────────────────
  const proceedAfterAuth = async (loggedInUser?: { role: string; clientId?: number }) => {
      // 1 — Fade out the login form; kick off video fetch concurrently
      //     so the network call overlaps the 750ms fade and adds no delay
      setFormVisible(false);
      const videoSrcPromise = fetchLoginVideoUrl(loggedInUser);
      await delay(750);

      // 2 — Black out the video overlay to mask the video swap
      setOverlayBlackout(true);
      await delay(500);

      // 3 — Await the video URL (almost certainly resolved by now) and swap
      const newSrc = await videoSrcPromise;
      if (videoRef.current) {
        videoRef.current.src = newSrc;
        videoRef.current.load();
        videoRef.current.play().catch(() => {});
      }
      await delay(200);

      // 4 — Lift the blackout to reveal the client's video
      setOverlayBlackout(false);
      await delay(4000); // let the video play for ~4 seconds before the GDPR modal appears

      // 5 — Pulse in the GDPR modal
      setShowGdpr(true);
  };

  const handleGdprAccept = () => {
    sessionStorage.setItem('gdprConsent', 'accepted');
    const userId = user?.id;
    const tourDone = userId ? localStorage.getItem(`tourDone-${userId}`) : null;
    if (!tourDone) {
      sessionStorage.setItem('fleetWizardPending', '1');
    }
    const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.username || null;
    consentAPI.log({
      consentType: 'GDPR_LOGIN',
      clientId: user?.clientId ?? null,
      driverName: fullName,
      details: 'Platform GDPR and data privacy notice accepted on login',
    policyVersion: 'v2.0.08-2026-10-08',
    }).catch(() => {});
    navigate('/my-feed');
  };

  return (
    <div className="login-container">
      {/* ── Background video ── */}
      <video
        ref={videoRef}
        className="video-background"
        autoPlay
        loop
        muted
        playsInline
        disablePictureInPicture
        controlsList="nodownload nofullscreen noremoteplayback"
        onContextMenu={(e) => e.preventDefault()}
      >
        <source src="/background-video.mp4" type="video/mp4" />
      </video>

      {/* ── Tinted overlay (goes fully black during video swap) ── */}
      <div className={`video-overlay${overlayBlackout ? ' blackout' : ''}`} />

      {showJwtNotice && (
        <div className="jwt-notice-banner">
          <span className="jwt-notice-text">
            This portal uses secure session tokens (JWT) stored in your browser&apos;s local storage to maintain your login. Your session data is encrypted in transit and automatically cleared on logout. By continuing to use this portal you acknowledge this.
          </span>
          <button type="button" className="jwt-notice-btn" onClick={handleJwtNoticeAcknowledge}>
            I Understand
          </button>
        </div>
      )}

      {/* ── Welcome splash ── */}
      <div className={`welcome-overlay${welcomeHidden ? ' hidden' : ''}`}>
        <div className="welcome-content">
          <img src={myshieldLogo} alt="Myshield" className="welcome-logo" />
          <p className="welcome-tagline">Myshield - Policy Management By Yourshield</p>
          <button className="welcome-enter-btn" onClick={handleEnter}>
            Enter Environment
          </button>
          <p className="welcome-hint">Authorised users only</p>
        </div>
      </div>

      {/* ── Login form ── */}
      <div className={`login-box${formVisible ? ' visible' : ''}`}>
        <div className="login-logo">
          <img src={myshieldLogo} alt="Myshield Logo" />
        </div>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="username">Username or Email</label>
            <input
              type="text"
              id="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              disabled={loading}
              placeholder="Use your username for support accounts"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={loading}
              placeholder="Enter your password"
            />
          </div>

          <button type="submit" className="login-button" disabled={loading}>
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        <div style={{ textAlign: 'center', marginTop: '14px' }}>
          <button
            type="button"
            onClick={() => navigate('/forgot-password')}
            style={{ background: 'none', border: 'none', color: '#4a5568', fontSize: '13px', cursor: 'pointer', textDecoration: 'underline' }}
          >
            Forgot your password?
          </button>
        </div>

        <div className="login-footer">
          <p>© 2026 Yourshield Ltd. All rights reserved.</p>
        </div>
      </div>

      {/* ── MFA code entry ── */}
      <div className={`login-box${mfaVisible ? ' visible' : ''}`}>
        <div className="login-logo">
          <img src={myshieldLogo} alt="Myshield Logo" />
        </div>

        <p className="mfa-prompt">A verification code has been sent to your email address. Enter it below to continue.</p>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleMfaSubmit}>
          <div className="form-group">
            <label htmlFor="mfa-code">Verification Code</label>
            <input
              type="text"
              id="mfa-code"
              value={mfaCode}
              onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              required
              disabled={loading}
              placeholder="Enter 6-digit code"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
            />
          </div>

          <button type="submit" className="login-button" disabled={loading || mfaCode.length !== 6}>
            {loading ? 'Verifying...' : 'Verify & Sign In'}
          </button>
        </form>

        <div className="login-footer">
          <p>Code expires in 10 minutes &nbsp;&middot;&nbsp; &copy; 2026 Yourshield Ltd. All rights reserved.</p>
        </div>
      </div>

      {showGdpr && <GdprConsentModal onAccept={handleGdprAccept} userRole={user?.role ?? ''} />}
    </div>
  );
};

export default Login;
